export { IntlProvider, useFormatter, useTranslations } from 'use-intl';
import { useAppContext } from '@ifixit/app/hooks';
import { invariant } from '@ifixit/helpers';
import { isStoreLocaleCode } from './helpers';
import { type LocaleCode } from './types';

// NOTE: The `use-intl` `AppConfig` augmentation that types message keys against
// iFixit's catalog (`EnMessages`) intentionally lives in the non-published
// ambient file `./use-intl-augment.d.ts` rather than here. Declaring it in this
// entry leaks it into the public `.d.ts`, forcing every external consumer's
// `use-intl`/`next-intl` usage to type against iFixit's messages. See that file
// for how internal apps opt in to typed message keys.

export function useStoreLocale(): LocaleCode {
   const { storeLocale } = useAppContext();
   invariant(isStoreLocaleCode(storeLocale), `Unsupported store locale code "${storeLocale}"`);
   return storeLocale;
}

export function useCustomMessage<T>(values: Record<LocaleCode, T>): T {
   const localeCode = useStoreLocale();
   return values[localeCode];
}

export function usePartialCustomMessage<T, D = null>(
   values: Partial<Record<LocaleCode, T>>,
   fallback?: D
): T | D {
   const localeCode = useStoreLocale();
   return values[localeCode] ?? fallback ?? (null as D);
}
